import React, { useState, useRef } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { trpc } from 'src/trpc';
import { ContainerTypeForm, ContainerTypeFormRef } from './ContainerTypeForm';
import { ContainerType } from 'src/trpc';
import { ContainerTypeZ } from '@libs/schemas';
import { useToast } from 'src/components/useToast';
import { ContainerImage } from '@libs/models';
import { Loader } from 'components/Loader';
import { Loader2 } from 'lucide-react';

interface ContainerTypeSheetProps {
  containerType?: ContainerType | null;
  trigger: React.ReactNode;
  onSuccess: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const ContainerTypeSheet: React.FC<ContainerTypeSheetProps> = ({
  containerType,
  trigger,
  onSuccess,
  open,
  onOpenChange,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef<ContainerTypeFormRef>(null);
  const isControlled = open !== undefined && onOpenChange !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const setOpen = isControlled ? onOpenChange : setInternalOpen;
  const utils = trpc.useUtils();

  const isEditing = !!containerType;
  const containerTypeId = containerType?._id as string | undefined;
  const createContainerTypeMutation = trpc.admin.containerType.createContainerType.useMutation();
  const updateContainerTypeMutation = trpc.admin.containerType.updateContainerType.useMutation();
  const uploadImagesMutation = trpc.admin.containerType.uploadContainerTypeImages.useMutation();
  const generateSignedUrlMutation = trpc.admin.containerType.generateSignedUploadUrl.useMutation();
  const finalizeUploadMutation = trpc.admin.containerType.finalizeImageUpload.useMutation();
  const deleteImageMutation = trpc.admin.containerType.deleteContainerTypeImage.useMutation();
  const { successToast, errorToast } = useToast();

  /**
   * Uploads a file directly to Cloud Storage using a signed URL
   */
  const uploadFileToSignedUrl = async (
    file: File,
    signedUrl: string,
    contentType: string,
  ): Promise<void> => {
    const response = await fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload file: ${response.statusText}`);
    }
  };

  /**
   * Uploads a single image file to Cloud Storage and returns the ContainerImage object
   * This is called when saving the form, not immediately on selection
   */
  const uploadImage = async (
    file: File,
    targetContainerTypeId: string,
  ): Promise<ContainerImage> => {
    // Step 1: Get signed URL from backend
    const signedUrlResult = await generateSignedUrlMutation.mutateAsync({
      containerTypeId: targetContainerTypeId,
      fileName: file.name,
      contentType: file.type,
      size: file.size,
    });

    // Step 2: Upload file directly to Cloud Storage
    await uploadFileToSignedUrl(file, signedUrlResult.signedUrl, file.type);

    // Step 3: Finalize the upload (make public, set metadata)
    const finalizedResult = await finalizeUploadMutation.mutateAsync({
      containerTypeId: targetContainerTypeId,
      fileName: signedUrlResult.fileName,
      contentType: file.type,
      size: file.size,
    });

    return {
      url: finalizedResult.url,
      fileName: finalizedResult.fileName,
      contentType: finalizedResult.contentType,
      size: finalizedResult.size,
      isMainImage: false,
      displayOrder: 0,
      archived: false,
    };
  };

  const handleSubmit = async (
    values: ContainerTypeZ & { images?: ContainerImage[]; uploadingFiles?: File[] },
  ) => {
    setIsLoading(true);
    try {
      let containerTypeId = containerType?._id as string;

      if (isEditing && containerType) {
        // For editing, we need to:
        // 1. Delete images that were removed from the images array
        // 2. Upload any queued files
        // 3. Update with complete images array
        
        // Find images that were deleted (in initial but not in final)
        const finalImageUrls = new Set(values.images?.map((img) => img.url) || []);
        const deletedImages = (initialValues?.images as ContainerImage[] | undefined)?.filter(
          (img) => !finalImageUrls.has(img.url) && img.fileName
        ) || [];

        // Delete removed images from Cloud Storage and database
        if (deletedImages.length > 0 && containerTypeId) {
          await Promise.all(
            deletedImages.map(async (img) => {
              if (img.fileName) {
                await deleteImageMutation.mutateAsync({
                  containerTypeId,
                  fileName: img.fileName,
                });
              }
            }),
          );
        }

        // Upload any queued files first
        let finalImages = values.images || [];
        const filesToUpload = values.uploadingFiles || [];
        if (filesToUpload.length > 0 && containerTypeId) {
          const uploadResults = await Promise.all(
            filesToUpload.map(async (file) => {
              return await uploadImage(file, containerTypeId);
            }),
          );

          // Add uploaded images to the final images array
          finalImages = [
            ...finalImages,
            ...uploadResults.map((img) => ({
              url: img.url,
              fileName: img.fileName,
              contentType: img.contentType,
              size: img.size,
              isMainImage: false,
              displayOrder: finalImages.length,
              archived: false,
            })),
          ];
        }

        // Update existing container type with complete images array
        const updateData = {
          ...values,
          _id: containerTypeId,
          images: finalImages,
        };
        delete updateData.uploadingFiles; // Remove from update data
        await updateContainerTypeMutation.mutateAsync(updateData);
      } else {
        // Create new container type
        const newContainerType = {
          ...values,
          archived: false,
          pricePerDay: Math.round(values.pricePerDay * 100),
          preAuthAmount: Math.round(values.preAuthAmount * 100),
          volume: values.volume || '',
          capacityDescription: values.capacityDescription || '',
          dimensions: values.dimensions || '',
          recommendedUse: values.recommendedUse || '',
          imageUrl: values.imageUrl || '',
          images: [], // Start with empty images array
        };
        delete newContainerType.uploadingFiles; // Remove from create data
        const result = await createContainerTypeMutation.mutateAsync(newContainerType);
        containerTypeId = result.createdContainerTypeId || '';
      }

      // For new container types, upload any queued files
      if (!isEditing && values.uploadingFiles && values.uploadingFiles.length > 0 && containerTypeId) {
        const uploadResults = await Promise.all(
          values.uploadingFiles.map(async (file) => {
            return await uploadImage(file, containerTypeId);
          }),
        );

        // Add uploaded images to container type
        await uploadImagesMutation.mutateAsync({
          containerTypeId,
          images: uploadResults.map((img) => ({
            url: img.url,
            fileName: img.fileName,
            contentType: img.contentType,
            size: img.size,
          })),
        });
      }

      // Clear uploading files and invalidate queries to refresh data
      formRef.current?.clearUploadingFiles();
      utils.admin.containerType.getAllContainerTypes.invalidate();
      if (containerTypeId) {
        utils.admin.containerType.getContainerType.invalidate(containerTypeId);
      }

      successToast({
        title: isEditing ? 'Dumpster Type Updated' : 'Dumpster Type Created',
        description: `The dumpster type has been successfully ${isEditing ? 'updated' : 'created'}.`,
      });

      setOpen(false);
      onSuccess();
    } catch (error) {
      errorToast({
        title: isEditing ? 'Update Failed' : 'Creation Failed',
        description: `There was an error ${isEditing ? 'updating' : 'creating'} the dumpster type. Please try again.`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    formRef.current?.submitForm();
  };

  const initialValues =
    isEditing && containerType
      ? {
          ...containerType,
          size: containerType.size as '10yard' | '20yard' | '30yard' | '40yard',
          images: containerType.images,
        }
      : undefined;

  return (
    <Sheet open={isOpen} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent className="p-0 sm:max-w-3xl h-full">
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          {/* Header */}
          <SheetHeader className="border-b bg-white px-6 py-4">
            <SheetTitle className="text-left text-text-light">
              {isEditing ? 'Edit Dumpster Type' : 'Create New Dumpster Type'}
            </SheetTitle>
          </SheetHeader>

          {/* Content Area - Scrollable */}
          <div className="relative overflow-y-auto p-6">
            <ContainerTypeForm
              key={containerType?._id || 'new'}
              ref={formRef}
              initialValues={initialValues}
              onSubmit={handleSubmit}
              isEditing={isEditing}
              disabled={isLoading}
              containerTypeId={containerTypeId}
            />
            {isLoading && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                <Loader
                  title={isEditing ? 'Updating dumpster type...' : 'Creating dumpster type...'}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <SheetFooter className="border-t bg-gray-50 px-6 py-4">
            <div className="flex w-full justify-end space-x-2">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={isLoading}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  handleSave();
                }}
                disabled={isLoading}
                className="bg-primary hover:bg-primary/90"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isLoading
                  ? isEditing
                    ? 'Updating...'
                    : 'Creating...'
                  : isEditing
                    ? 'Update Dumpster Type'
                    : 'Create Dumpster Type'}
              </Button>
            </div>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};
